import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';
import playerErrors from '../../../errors_json/player_errors.json';
import { ErrorCode } from '../utils/ErrorCodes';
import { KEYS } from '../utils/constants/storage.constants';
import { ConfigService } from './config.service';
import { FailureMessageModel } from './evergent/base/failure-message.model';
import { LocalStorageService } from './storage/local-storage.service';

export type ErrorCodeEntry = ErrorCode[string];

@Injectable()
export class ErrorCodeTranslationService {
  errors: ErrorCode;
  lang = 'en';

  constructor(
    private configService: ConfigService,
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private readonly newrelicService: NewrelicService
  ) {
    this.getDisplayLanguage();
    this.getErrorData();
  }

  public getError(errorResponse: FailureMessageModel): ErrorCodeEntry {
    try {
      const errorCode = errorResponse.errorCode;
      if (this.errors[errorCode]) {
        return this.errors[errorCode];
      } else {
        return {
          errorCode: errorResponse.errorCode,
          description: errorResponse.errorMessage,
          en: errorResponse.errorMessage,
          ta: errorResponse.errorMessage,
          te: errorResponse.errorMessage,
        };
      }
    } catch (e) {
      return {
        errorCode: errorResponse.errorCode,
        description: errorResponse.errorMessage,
        en: errorResponse.errorMessage,
        ta: errorResponse.errorMessage,
        te: errorResponse.errorMessage,
      };
    }
  }

  public async getErrorByCode(
    errorCode: string,
    errorMessage: string
  ): Promise<ErrorCodeEntry> {
    try {
      this.getDisplayLanguage();
      if (!this.errors) {
        await this.getErrorData();
      }
      return this.errorCodeExists(errorCode)
        ? Promise.resolve(this.errors[errorCode])
        : Promise.resolve({
            errorCode,
            description: errorMessage,
            en: errorMessage,
            ta: errorMessage,
            te: errorMessage,
          });
    } catch (e) {
      this.newrelicService.sendErrorEvent({
        error: e,
      });
      return {
        errorCode,
        description: errorMessage,
        en: errorMessage,
        ta: errorMessage,
        te: errorMessage,
      };
    }
  }

  public getLocalizedMessage(error: ErrorCodeEntry): string {
    return error[this.lang] || error?.en;
  }

  private async getErrorData(): Promise<void> {
    try {
      const remoteErrors = await this.getErrorCodeTranslation();
      this.errors = this.mergeMissingKeys(
        playerErrors,
        remoteErrors
      ) as ErrorCode;
    } catch (error: any) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      this.errors = playerErrors as ErrorCode;
    }
    return Promise.resolve();
  }

  private mergeMissingKeys(local: any, remote: any): any {
    if (!local) return remote;
    if (!remote) return local;

    const result = { ...remote };

    for (const key in local) {
      if (local.hasOwnProperty(key)) {
        if (!result.hasOwnProperty(key)) {
          result[key] = local[key];
        } else if (
          typeof local[key] === 'object' &&
          local[key] !== null &&
          typeof result[key] === 'object' &&
          result[key] !== null &&
          !Array.isArray(local[key]) &&
          !Array.isArray(result[key])
        ) {
          result[key] = this.mergeMissingKeys(local[key], result[key]);
        }
      }
    }
    return result;
  }

  private errorCodeExists(errorCode: string): boolean {
    return !!this.errors[errorCode];
  }

  private getErrorCodeTranslation(): Promise<ErrorCode> {
    const url =
      this.configService.applicationConfig.smarttvV2ErrorCodeTranslation;
    return this.http.get<ErrorCode>(url).toPromise();
  }

  private getDisplayLanguage(): void {
    this.lang = this.localStorage.getItem(KEYS.DISPLAY_LANGUAGE) || 'en';
  }
}
